import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Star, ChevronDown, Lock, ChevronRight } from 'lucide-react';

const SPORTS = [
  { id: 'nfl',              label: 'NFL',                emoji: '🏈' },
  { id: 'nba',              label: 'NBA',                emoji: '🏀' },
  { id: 'mlb',              label: 'MLB',                emoji: '⚾' },
  { id: 'nhl',              label: 'NHL',                emoji: '🏒' },
  { id: 'ncaafb',           label: 'College Football',   emoji: '🏈' },
  { id: 'ncaamb',           label: 'College Basketball', emoji: '🏀' },
  { id: 'epl',              label: 'Premier League',     emoji: '⚽' },
  { id: 'mls',              label: 'MLS',                emoji: '⚽' },
  { id: 'champions_league', label: 'Champions League',   emoji: '⚽' },
  { id: 'mma',              label: 'UFC / MMA',          emoji: '🥊' },
];

// Complete team lists for each sport
const ALL_TEAMS = {
  nfl: [
    'Arizona Cardinals','Atlanta Falcons','Baltimore Ravens','Buffalo Bills',
    'Carolina Panthers','Chicago Bears','Cincinnati Bengals','Cleveland Browns',
    'Dallas Cowboys','Denver Broncos','Detroit Lions','Green Bay Packers',
    'Houston Texans','Indianapolis Colts','Jacksonville Jaguars','Kansas City Chiefs',
    'Las Vegas Raiders','Los Angeles Chargers','Los Angeles Rams','Miami Dolphins',
    'Minnesota Vikings','New England Patriots','New Orleans Saints','New York Giants',
    'New York Jets','Philadelphia Eagles','Pittsburgh Steelers','San Francisco 49ers',
    'Seattle Seahawks','Tampa Bay Buccaneers','Tennessee Titans','Washington Commanders',
  ],
  nba: [
    'Atlanta Hawks','Boston Celtics','Brooklyn Nets','Charlotte Hornets',
    'Chicago Bulls','Cleveland Cavaliers','Dallas Mavericks','Denver Nuggets',
    'Detroit Pistons','Golden State Warriors','Houston Rockets','Indiana Pacers',
    'Los Angeles Clippers','Los Angeles Lakers','Memphis Grizzlies','Miami Heat',
    'Milwaukee Bucks','Minnesota Timberwolves','New Orleans Pelicans','New York Knicks',
    'Oklahoma City Thunder','Orlando Magic','Philadelphia 76ers','Phoenix Suns',
    'Portland Trail Blazers','Sacramento Kings','San Antonio Spurs','Toronto Raptors',
    'Utah Jazz','Washington Wizards',
  ],
  mlb: [
    'Arizona Diamondbacks','Atlanta Braves','Baltimore Orioles','Boston Red Sox',
    'Chicago Cubs','Chicago White Sox','Cincinnati Reds','Cleveland Guardians',
    'Colorado Rockies','Detroit Tigers','Houston Astros','Kansas City Royals',
    'Los Angeles Angels','Los Angeles Dodgers','Miami Marlins','Milwaukee Brewers',
    'Minnesota Twins','New York Mets','New York Yankees','Oakland Athletics',
    'Philadelphia Phillies','Pittsburgh Pirates','San Diego Padres','San Francisco Giants',
    'Seattle Mariners','St. Louis Cardinals','Tampa Bay Rays','Texas Rangers',
    'Toronto Blue Jays','Washington Nationals',
  ],
  nhl: [
    'Anaheim Ducks','Arizona Coyotes','Boston Bruins','Buffalo Sabres',
    'Calgary Flames','Carolina Hurricanes','Chicago Blackhawks','Colorado Avalanche',
    'Columbus Blue Jackets','Dallas Stars','Detroit Red Wings','Edmonton Oilers',
    'Florida Panthers','Los Angeles Kings','Minnesota Wild','Montreal Canadiens',
    'Nashville Predators','New Jersey Devils','New York Islanders','New York Rangers',
    'Ottawa Senators','Philadelphia Flyers','Pittsburgh Penguins','San Jose Sharks',
    'Seattle Kraken','St. Louis Blues','Tampa Bay Lightning','Toronto Maple Leafs',
    'Utah Hockey Club','Vancouver Canucks','Vegas Golden Knights','Washington Capitals',
    'Winnipeg Jets',
  ],
  ncaafb: [
    'Alabama','Arizona','Arizona State','Arkansas','Auburn','Baylor','Boston College',
    'California','Clemson','Colorado','Duke','Florida','Florida State','Georgia',
    'Georgia Tech','Illinois','Indiana','Iowa','Iowa State','Kansas','Kansas State',
    'Kentucky','LSU','Maryland','Michigan','Michigan State','Minnesota','Mississippi State',
    'Missouri','Nebraska','North Carolina','North Carolina State','Northwestern','Notre Dame',
    'Ohio State','Oklahoma','Oklahoma State','Ole Miss','Oregon','Oregon State',
    'Penn State','Pittsburgh','Purdue','Rutgers','SMU','Stanford','Syracuse','TCU',
    'Tennessee','Texas','Texas A&M','Texas Tech','UCLA','USC','Utah','Vanderbilt',
    'Virginia','Virginia Tech','Washington','Washington State','West Virginia','Wisconsin',
  ],
  ncaamb: [
    'Alabama','Arizona','Arizona State','Arkansas','Auburn','Baylor','Boston College',
    'Butler','Cincinnati','Clemson','Colorado','Connecticut','Creighton','DePaul',
    'Duke','Florida','Florida State','Georgetown','Georgia','Georgia Tech','Gonzaga',
    'Houston','Illinois','Indiana','Iowa','Iowa State','Kansas','Kansas State',
    'Kentucky','Louisville','Loyola Chicago','LSU','Marquette','Maryland','Memphis',
    'Michigan','Michigan State','Minnesota','Missouri','Nebraska','North Carolina',
    'Northwestern','Notre Dame','Ohio State','Oklahoma','Oregon','Penn State',
    'Pittsburgh','Providence','Purdue','Rutgers','San Diego State','Seton Hall',
    'St. John\'s','Stanford','Syracuse','TCU','Tennessee','Texas','Texas A&M',
    'Texas Tech','UCLA','USC','Utah','Vanderbilt','Villanova','Virginia',
    'Virginia Tech','Wake Forest','Washington','West Virginia','Wisconsin','Xavier',
  ],
  epl: [
    'Arsenal','Aston Villa','Bournemouth','Brentford','Brighton',
    'Chelsea','Crystal Palace','Everton','Fulham','Ipswich Town',
    'Leicester City','Liverpool','Manchester City','Manchester United','Newcastle United',
    'Nottingham Forest','Southampton','Tottenham Hotspur','West Ham United','Wolverhampton',
  ],
  mls: [
    'Atlanta United','Austin FC','Charlotte FC','Chicago Fire','FC Cincinnati',
    'Colorado Rapids','Columbus Crew','D.C. United','FC Dallas','Houston Dynamo',
    'Inter Miami','LA Galaxy','LAFC','Minnesota United','CF Montréal',
    'Nashville SC','New England Revolution','New York City FC','New York Red Bulls',
    'Orlando City','Philadelphia Union','Portland Timbers','Real Salt Lake',
    'San Jose Earthquakes','Seattle Sounders','Sporting Kansas City','St. Louis City',
    'Toronto FC','Vancouver Whitecaps',
  ],
  champions_league: [
    'Arsenal','Aston Villa','Atletico Madrid','Barcelona','Bayer Leverkusen',
    'Bayern Munich','Benfica','Bologna','Borussia Dortmund','Brest',
    'Celtic','Club Brugge','Dinamo Zagreb','Feyenoord','Girona',
    'Inter Milan','Juventus','Lille','Liverpool','Manchester City',
    'Monaco','PSG','PSV Eindhoven','RB Leipzig','Real Madrid',
    'Red Bull Salzburg','Shakhtar Donetsk','Slavia Prague','Sparta Prague',
    'Sporting CP','Sturm Graz','VfB Stuttgart','Young Boys',
  ],
  mma: [
    'Islam Makhachev','Jon Jones','Alex Pereira','Leon Edwards','Tom Aspinall',
    "Sean O'Malley",'Dricus Du Plessis','Ilia Topuria','Alexandre Pantoja',
    'Merab Dvalishvili','Charles Oliveira','Max Holloway','Conor McGregor',
    'Khamzat Chimaev','Robert Whittaker','Ciryl Gane','Paddy Pimblett',
    'Dustin Poirier','Justin Gaethje','Michael Chandler','Colby Covington',
    'Belal Muhammad','Valentina Shevchenko','Zhang Weili','Julianna Pena',
    'Amanda Nunes','Alexa Grasso','Sean Strickland','Jamahal Hill',
    'Jiri Prochazka','Brandon Moreno','Deiveson Figueiredo','Calvin Kattar',
    'Dan Hooker','Renato Moicano','Bryce Mitchell','Edson Barboza',
  ],
};

const TIER_ORDER = ['free', 'basic', 'standard', 'premium'];
function tierAtLeast(userTier, required) {
  return TIER_ORDER.indexOf(userTier) >= TIER_ORDER.indexOf(required);
}

function GameCard({ game }) {
  const isLive = game.status === 'inprogress' || game.status === 'halftime';
  const isFinal = game.status === 'closed' || game.status === 'complete';
  const homeScore = game.home_points ?? null;
  const awayScore = game.away_points ?? null;
  const hasScore = homeScore !== null && awayScore !== null;
  const homeAlias = game.home?.alias || (game.home?.name || 'HME').slice(0, 3).toUpperCase();
  const awayAlias = game.away?.alias || (game.away?.name || 'AWY').slice(0, 3).toUpperCase();
  const homeLogo = game.home?.logo || null;
  const awayLogo = game.away?.logo || null;
  const scheduledTime = game.scheduled
    ? new Date(game.scheduled).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div style={{
      borderRadius: 14,
      border: isLive ? '1px solid rgba(34,197,94,0.35)' : '1px solid rgba(255,255,255,0.07)',
      background: isLive ? 'rgba(34,197,94,0.05)' : 'rgba(255,255,255,0.02)',
      padding: '10px 12px', marginBottom: 8,
      width: '100%', boxSizing: 'border-box',
    }}>
      {isLive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: '#22c55e', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Live {game.quarter ? `· Q${game.quarter}` : ''} {game.clock ? `· ${game.clock}` : ''}
          </span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 6, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {awayLogo && <img src={awayLogo} alt={awayAlias} style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{awayAlias}</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.away?.name || ''}</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0, padding: '0 4px' }}>
          {hasScore ? (
            <p style={{ fontSize: 18, fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1, whiteSpace: 'nowrap' }}>{awayScore}–{homeScore}</p>
          ) : scheduledTime ? (
            <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: 0, whiteSpace: 'nowrap' }}>{scheduledTime}</p>
          ) : (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: 0 }}>TBD</p>
          )}
          {isFinal && <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', margin: '2px 0 0', textTransform: 'uppercase' }}>Final</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', minWidth: 0 }}>
          <div style={{ minWidth: 0, textAlign: 'right' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{homeAlias}</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.home?.name || ''}</p>
          </div>
          {homeLogo && <img src={homeLogo} alt={homeAlias} style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />}
        </div>
      </div>
    </div>
  );
}

function SportSection({ sport, favoriteTeams, scores, hasFavorites }) {
  const [expanded, setExpanded] = useState(true);
  const games = scores[sport.id] || [];
  const favIds = favoriteTeams[sport.id] || [];

  // When the user has any favorites set, hide sports where they picked no teams
  if (hasFavorites && favIds.length === 0) return null;

  const isFavGame = (g) => favIds.some(fav =>
    (g.home?.name || '').toLowerCase().includes(fav.toLowerCase()) ||
    (g.away?.name || '').toLowerCase().includes(fav.toLowerCase()) ||
    (g.home?.alias || '').toLowerCase() === fav.toLowerCase() ||
    (g.away?.alias || '').toLowerCase() === fav.toLowerCase()
  );

  // Favorites at top, then rest
  const favGames = favIds.length > 0 ? games.filter(isFavGame) : [];
  const otherGames = favIds.length > 0 ? games.filter(g => !isFavGame(g)) : games;
  const sorted = [...favGames, ...otherGames];

  if (sorted.length === 0) return null;

  return (
    <div style={{ marginBottom: 20, width: '100%', boxSizing: 'border-box' }}>
      <button onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px' }}>
        <span style={{ fontSize: 16 }}>{sport.emoji}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{sport.label}</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 4 }}>
          {sorted.length} game{sorted.length !== 1 ? 's' : ''}
        </span>
        {favGames.length > 0 && (
          <span style={{ fontSize: 10, color: '#facc15', fontWeight: 700, marginLeft: 2 }}>
            ★ {favGames.length} fav
          </span>
        )}
        <ChevronDown size={14} style={{ color: 'rgba(255,255,255,0.3)', marginLeft: 'auto', transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
      </button>
      {expanded && sorted.map((game, i) => <GameCard key={game.id || i} game={game} />)}
    </div>
  );
}

function TeamPicker({ sport, favoriteTeams, onToggle }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const allTeams = ALL_TEAMS[sport.id] || [];
  const favIds = favoriteTeams[sport.id] || [];

  const filtered = search.trim()
    ? allTeams.filter(t => t.toLowerCase().includes(search.toLowerCase()))
    : allTeams;

  return (
    <div style={{ marginBottom: 4, width: '100%', boxSizing: 'border-box' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}>
        <span style={{ fontSize: 15 }}>{sport.emoji}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{sport.label}</span>
        {favIds.length > 0 && (
          <span style={{ fontSize: 10, color: '#facc15', fontWeight: 700, marginLeft: 2 }}>
            {favIds.length} selected
          </span>
        )}
        <ChevronRight size={12} style={{ color: 'rgba(255,255,255,0.3)', marginLeft: 'auto', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={{ paddingBottom: 12, paddingLeft: 8 }}>
          {/* Search box */}
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${sport.label} teams...`}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 12px', borderRadius: 10, marginBottom: 8,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', fontSize: 12, outline: 'none',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
            {filtered.map(team => {
              const isFav = favIds.includes(team);
              return (
                <button key={team} onClick={() => onToggle(sport.id, team)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px', borderRadius: 20,
                    border: isFav ? '1px solid rgba(250,204,21,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    background: isFav ? 'rgba(250,204,21,0.1)' : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    color: isFav ? '#facc15' : 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                  }}>
                  {isFav && <Star size={9} style={{ fill: '#facc15', color: '#facc15' }} />}
                  {team}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Scores() {
  const { user, getToken } = useAuth();
  const [userTier, setUserTier] = useState('free');
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [favoriteTeams, setFavoriteTeams] = useState({});
  const [showPicker, setShowPicker] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const saved = localStorage.getItem('qude_favorite_teams');
    if (saved) { try { setFavoriteTeams(JSON.parse(saved)); } catch {} }
  }, []);

  useEffect(() => {
    if (!user) return;
    const loadTier = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setUserTier(data.subscription_tier || 'free');
      } catch {}
    };
    loadTier();
  }, [user, getToken, BACKEND_URL]);

  const fetchScores = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const results = await Promise.allSettled(
        SPORTS.map(s => fetch(`${BACKEND_URL}/api/scores/${s.id}`, { headers }).then(r => r.json()))
      );
      const newScores = {};
      SPORTS.forEach((s, i) => {
        const result = results[i];
        newScores[s.id] = result.status === 'fulfilled' && result.value?.games ? result.value.games : [];
      });
      setScores(newScores);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Scores fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken, BACKEND_URL]);

  useEffect(() => {
    if (!user || !tierAtLeast(userTier, 'basic')) return;
    fetchScores();
    const interval = setInterval(fetchScores, 60000);
    return () => clearInterval(interval);
  }, [user, userTier, fetchScores]);

  const handleToggleFavorite = (sportId, teamName) => {
    setFavoriteTeams(prev => {
      const current = prev[sportId] || [];
      const updated = current.includes(teamName)
        ? current.filter(t => t !== teamName)
        : [...current, teamName];
      const next = { ...prev, [sportId]: updated };
      localStorage.setItem('qude_favorite_teams', JSON.stringify(next));
      return next;
    });
  };

  const hasFavorites = Object.values(favoriteTeams).some(arr => arr.length > 0);
  const sportsWithGames = SPORTS.filter(s => (scores[s.id] || []).length > 0);

  if (!tierAtLeast(userTier, 'basic')) {
    return (
      <div style={{ maxWidth: 480, width: '100%', margin: '0 auto', paddingBottom: 40, boxSizing: 'border-box', overflowX: 'hidden' }}>
        <div style={{ marginBottom: 20, paddingTop: 4 }}>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 4px' }}>🏆 Sports</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>Live scores · Your teams first</p>
        </div>
        <div style={{ borderRadius: 20, border: '1px solid rgba(124,58,237,0.3)', background: 'linear-gradient(135deg, rgba(124,58,237,0.1), rgba(74,158,255,0.05))', padding: '32px 24px', textAlign: 'center' }}>
          <Lock size={32} style={{ color: '#a78bfa', margin: '0 auto 16px', display: 'block' }} />
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Sports on Basic+</h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', margin: '0 0 24px', lineHeight: 1.6 }}>
            Live scores for NFL, NBA, MLB, NHL, soccer and more — personalized to your favorite teams. Starting at $3.99/month.
          </p>
          <a href="/pricing" style={{ textDecoration: 'none' }}>
            <button style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', border: 'none', borderRadius: 20, padding: '12px 28px', fontSize: 14, fontWeight: 800, color: '#fff', cursor: 'pointer' }}>
              Upgrade to Basic
            </button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, width: '100%', margin: '0 auto', paddingBottom: 40, boxSizing: 'border-box', overflowX: 'hidden' }}>

      {/* Header */}
      <div style={{ marginBottom: 20, paddingTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: 0 }}>🏆 Sports</h1>
          {lastUpdated && (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', margin: 0 }}>
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>Today's games</p>
      </div>

      {/* Team picker */}
      <div style={{ borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', background: '#0f0f0f', padding: 16, marginBottom: 24, width: '100%', boxSizing: 'border-box' }}>
        <button onClick={() => setShowPicker(p => !p)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 2px' }}>
          <Star size={13} style={{ color: '#facc15', fill: hasFavorites ? '#facc15' : 'none' }} />
          <span style={{ fontSize: 12, fontWeight: 800, color: hasFavorites ? '#facc15' : 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            My Teams
          </span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>
            {hasFavorites ? '— tap a sport to edit' : '— select your teams'}
          </span>
          <ChevronDown size={13} style={{ color: 'rgba(255,255,255,0.3)', marginLeft: 'auto', transform: showPicker ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </button>

        {showPicker && (
          <div style={{ marginTop: 12, width: '100%', boxSizing: 'border-box' }}>
            {SPORTS.map(sport => (
              <TeamPicker
                key={sport.id}
                sport={sport}
                favoriteTeams={favoriteTeams}
                onToggle={handleToggleFavorite}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scores */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '40px 0', justifyContent: 'center', color: 'rgba(255,255,255,0.3)' }}>
          <div style={{ width: 16, height: 16, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          Loading today's games...
        </div>
      ) : sportsWithGames.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ fontSize: 32, margin: '0 0 12px' }}>🏆</p>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14, margin: '0 0 6px' }}>No games today</p>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, margin: 0 }}>Check back tomorrow</p>
        </div>
      ) : (
        <>
          {hasFavorites && (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '0 0 16px' }}>
              Showing your favorite teams only
            </p>
          )}
          {SPORTS.map(sport => (
            <SportSection key={sport.id} sport={sport} favoriteTeams={favoriteTeams} scores={scores} hasFavorites={hasFavorites} />
          ))}
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
